import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Shield, Briefcase, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';

const UsersAdd = () => {
  const [loading, setLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      role: 'USER',
      canAccessSettings: false,
      canAccessUsers: false,
      canAccessDepartments: false,
      canAccessCases: false,
      canAccessHr: false,
      canAccessDoctorAbsences: false,
      canAccessPrints: false,
      canAccessUmowyCru: false,
      canAccessTrainings: false,
      canAccessInvoices: false,
      canAccessSubstitutions: false,
      canAccessMfaSettings: false,
      canAccessLeaveApprovals: false
    }
  });

  const isSuperAdmin = currentUser?.role === 'SUPERADMIN';

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      toast.error('Błąd podczas pobierania działów');
      console.error('Error fetching departments:', error);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const userData = {
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber || null,
        position: data.position || null,
        kadryEmail: data.kadryEmail || null,
        role: data.role,
        departmentId: data.departmentId || null,
        active: true
      };

      // Dodaj uprawnienia tylko jeśli użytkownik jest Super Admin
      if (isSuperAdmin) {
        userData.canAccessSettings = data.canAccessSettings || false;
        userData.canAccessUsers = data.canAccessUsers || false;
        userData.canAccessDepartments = data.canAccessDepartments || false;
        userData.canAccessCases = data.canAccessCases || false;
        userData.canAccessHr = data.canAccessHr || false;
        userData.canAccessDoctorAbsences = data.canAccessDoctorAbsences || false;
        userData.canAccessPrints = data.canAccessPrints || false;
        userData.canAccessUmowyCru = data.canAccessUmowyCru || false;
        userData.canAccessTrainings = data.canAccessTrainings || false;
        userData.canAccessInvoices = data.canAccessInvoices || false;
        userData.canAccessSubstitutions = data.canAccessSubstitutions || false;
        userData.canAccessMfaSettings = data.canAccessMfaSettings || false;
        userData.canAccessLeaveApprovals = data.canAccessLeaveApprovals || false;
        userData.canAccountInvoice = data.canAccountInvoice || false;
        userData.isManager = data.isManager || false;
        userData.isDirector = data.isDirector || false;
        userData.isPreviewMode = data.isPreviewMode || false;
        userData.canCheckMerit = data.canCheckMerit || false;
        userData.canDoPreliminaryCheck = data.canDoPreliminaryCheck || false;
        userData.canCheckFormalAccounting = data.canCheckFormalAccounting || false;
        userData.canAcceptInvoices = data.canAcceptInvoices || false;
        userData.canViewSignedInvoices = data.canViewSignedInvoices || false;
        userData.canViewAccountedInvoices = data.canViewAccountedInvoices || false;
        userData.canDeleteDescriptionsAndSignatures = data.canDeleteDescriptionsAndSignatures || false;
        userData.canDeleteInvoices = data.canDeleteInvoices || false;
        userData.canEditInvoices = data.canEditInvoices || false;
        userData.canEditPaymentDueDate = data.canEditPaymentDueDate || false;
        userData.canUseCostCenter = data.canUseCostCenter || false;
      }
      userData.emailOnAssignment = data.emailOnAssignment !== false;
      userData.emailOnReturn = data.emailOnReturn !== false;

      await api.post('/users', userData);
      toast.success('Użytkownik został dodany pomyślnie');
      reset();
      navigate('/app/users/list');
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas dodawania użytkownika';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl">Dodaj użytkownika</h2>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="firstName" className="block text-sm text-gray-700 mb-2">
                Imię <span className="text-red-500">*</span>
              </label>
              <input
                {...register('firstName', {
                  required: 'Imię jest wymagane',
                  minLength: {
                    value: 2,
                    message: 'Imię musi mieć co najmniej 2 znaki'
                  }
                })}
                type="text"
                id="firstName"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Jan"
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm text-gray-700 mb-2">
                Nazwisko <span className="text-red-500">*</span>
              </label>
              <input
                {...register('lastName', {
                  required: 'Nazwisko jest wymagane',
                  minLength: {
                    value: 2,
                    message: 'Nazwisko musi mieć co najmniej 2 znaki'
                  }
                })}
                type="text"
                id="lastName"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="Kowalski"
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-sm text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              {...register('email', {
                required: 'Email jest wymagany',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Nieprawidłowy adres email'
                }
              })}
              type="email"
              id="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="jan.kowalski@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="phoneNumber" className="block text-sm text-gray-700 mb-2">
                Numer telefonu
              </label>
              <input
                {...register('phoneNumber', {
                  pattern: {
                    value: /^[0-9+\-\s()]*$/,
                    message: 'Nieprawidłowy format numeru telefonu'
                  }
                })}
                type="tel"
                id="phoneNumber"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="+48 123 456 789"
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="position" className="block text-sm text-gray-700 mb-2">
                Stanowisko
              </label>
              <input
                {...register('position')}
                type="text"
                id="position"
                list="positionOptions"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="np. Kierownik, Zastępca"
              />
              <datalist id="positionOptions">
                <option value="Kierownik" />
                <option value="Zastępca" />
                <option value="Specjalista" />
                <option value="Starszy Specjalista" />
                <option value="Młodszy Specjalista" />
                <option value="Asystent" />
                <option value="Koordynator" />
                <option value="Dyrektor" />
                <option value="Wicedyrektor" />
                <option value="Sekretarz" />
                <option value="Pracownik" />
              </datalist>
            </div>

            <div>
              <label htmlFor="kadryEmail" className="block text-sm text-gray-700 mb-2">
                E-mail kadrowy <span className="text-gray-500 text-xs">(jak w systemie kadrowym - wiąże konto z grafikiem)</span>
              </label>
              <input
                {...register('kadryEmail')}
                type="email"
                id="kadryEmail"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="np. jan.kowalski@przyklad.pl"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="password" className="block text-sm text-gray-700 mb-2">
                Hasło <span className="text-red-500">*</span>
              </label>
              <input
                {...register('password', {
                  required: 'Hasło jest wymagane',
                  minLength: {
                    value: 6,
                    message: 'Hasło musi mieć co najmniej 6 znaków'
                  }
                })}
                type="password"
                id="password"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm text-gray-700 mb-2">
                Rola <span className="text-red-500">*</span>
              </label>
              <select
                {...register('role', {
                  required: 'Rola jest wymagana'
                })}
                id="role"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="USER">Użytkownik</option>
                <option value="SEKRETARIAT">Sekretariat</option>
                <option value="ADMIN">Administrator</option>
                <option value="SUPERADMIN">Super Administrator</option>
              </select>
              {errors.role && (
                <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="departmentId" className="block text-sm text-gray-700 mb-2">
              Dział
            </label>
            {departmentsLoading ? (
              <div className="flex items-center">
                <LoadingSpinner size="sm" />
                <span className="ml-2 text-sm text-gray-500">Ładowanie działów...</span>
              </div>
            ) : (
              <select
                {...register('departmentId')}
                id="departmentId"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">Brak działu</option>
                {departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Sekcja uprawnień - tylko dla Super Admin */}
          {isSuperAdmin && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Uprawnienia dostępu</h3>
              
              {/* Kategoria: Dostęp do modułów */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-blue-600" />
                  Dostęp do modułów systemu
                </h4>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      <input
                        {...register('canAccessSettings')}
                        type="checkbox"
                        id="canAccessSettings"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessSettings" className="ml-2 block text-sm text-gray-900">
                        Dostęp do ustawień
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessUsers')}
                        type="checkbox"
                        id="canAccessUsers"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessUsers" className="ml-2 block text-sm text-gray-900">
                        Dostęp do użytkowników
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessDepartments')}
                        type="checkbox"
                        id="canAccessDepartments"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessDepartments" className="ml-2 block text-sm text-gray-900">
                        Dostęp do działów
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessCases')}
                        type="checkbox"
                        id="canAccessCases"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessCases" className="ml-2 block text-sm text-gray-900">
                        Dostęp do spraw
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessHr')}
                        type="checkbox"
                        id="canAccessHr"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessHr" className="ml-2 block text-sm text-gray-900">
                        Dostęp do kadr
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessDoctorAbsences')}
                        type="checkbox"
                        id="canAccessDoctorAbsences"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessDoctorAbsences" className="ml-2 block text-sm text-gray-900">
                        Dostęp do nieobecności lekarzy
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessPrints')}
                        type="checkbox"
                        id="canAccessPrints"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessPrints" className="ml-2 block text-sm text-gray-900">
                        Dostęp do wydruków
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessInvoices')}
                        type="checkbox"
                        id="canAccessInvoices"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessInvoices" className="ml-2 block text-sm text-gray-900">
                        Dostęp do faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessSubstitutions')}
                        type="checkbox"
                        id="canAccessSubstitutions"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessSubstitutions" className="ml-2 block text-sm text-gray-900">
                        Dostęp do zastępstw
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessMfaSettings')}
                        type="checkbox"
                        id="canAccessMfaSettings"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessMfaSettings" className="ml-2 block text-sm text-gray-900">
                        Podpis elektroniczny (pozycja w menu)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessLeaveApprovals')}
                        type="checkbox"
                        id="canAccessLeaveApprovals"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessLeaveApprovals" className="ml-2 block text-sm text-gray-900">
                        Zatwierdzanie wniosków urlopowych (kadry)
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessUmowyCru')}
                        type="checkbox"
                        id="canAccessUmowyCru"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessUmowyCru" className="ml-2 block text-sm text-gray-900">
                        Dostęp do umów CRU
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAccessTrainings')}
                        type="checkbox"
                        id="canAccessTrainings"
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccessTrainings" className="ml-2 block text-sm text-gray-900">
                        Zarządzanie szkoleniami
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Kategoria: Role organizacyjne */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Briefcase className="h-4 w-4 mr-2 text-purple-600" />
                  Role organizacyjne
                </h4>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      <input
                        {...register('isManager')}
                        type="checkbox"
                        id="isManager"
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isManager" className="ml-2 block text-sm text-gray-900">
                        Kierownik działu
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('isDirector')}
                        type="checkbox"
                        id="isDirector"
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isDirector" className="ml-2 block text-sm text-gray-900">
                        Dyrektor
                      </label>
                    </div>
                    <div className="flex items-center md:col-span-2">
                      <input
                        {...register('isPreviewMode')}
                        type="checkbox"
                        id="isPreviewMode"
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isPreviewMode" className="ml-2 block text-sm text-gray-900">
                        Tryb – PODGLĄD
                        <span className="block text-xs text-gray-500 mt-0.5">
                          Czytanie i otwieranie załączników bez możliwości usuwania. Brak dostępu do ustawień, użytkowników i działów.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Kategoria: Uprawnienia fakturowe */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <Mail className="h-4 w-4 mr-2 text-green-600" />
                  Uprawnienia fakturowe
                </h4>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex items-center">
                      <input
                        {...register('canAccountInvoice')}
                        type="checkbox"
                        id="canAccountInvoice"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAccountInvoice" className="ml-2 block text-sm text-gray-900">
                        Księgowanie faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canCheckMerit')}
                        type="checkbox"
                        id="canCheckMerit"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canCheckMerit" className="ml-2 block text-sm text-gray-900">
                        Sprawdzanie merytoryczne faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canDoPreliminaryCheck')}
                        type="checkbox"
                        id="canDoPreliminaryCheck"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canDoPreliminaryCheck" className="ml-2 block text-sm text-gray-900">
                        Dokonano wstępnej kontroli
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canCheckFormalAccounting')}
                        type="checkbox"
                        id="canCheckFormalAccounting"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canCheckFormalAccounting" className="ml-2 block text-sm text-gray-900">
                        Sprawdzanie formalno-rachunkowe faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canAcceptInvoices')}
                        type="checkbox"
                        id="canAcceptInvoices"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canAcceptInvoices" className="ml-2 block text-sm font-semibold text-gray-900">
                        Zgoda na akceptację faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canViewSignedInvoices')}
                        type="checkbox"
                        id="canViewSignedInvoices"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canViewSignedInvoices" className="ml-2 block text-sm text-gray-900">
                        Widok zakładki Podpisane
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canViewAccountedInvoices')}
                        type="checkbox"
                        id="canViewAccountedInvoices"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canViewAccountedInvoices" className="ml-2 block text-sm text-gray-900">
                        Widok zakładki Zaksięgowane
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canDeleteDescriptionsAndSignatures')}
                        type="checkbox"
                        id="canDeleteDescriptionsAndSignatures"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canDeleteDescriptionsAndSignatures" className="ml-2 block text-sm text-gray-900">
                        Usuwanie opisów i podpisów faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canDeleteInvoices')}
                        type="checkbox"
                        id="canDeleteInvoices"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canDeleteInvoices" className="ml-2 block text-sm text-gray-900">
                        Usuwanie faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canEditInvoices')}
                        type="checkbox"
                        id="canEditInvoices"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canEditInvoices" className="ml-2 block text-sm text-gray-900">
                        Edycja faktur
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canEditPaymentDueDate')}
                        type="checkbox"
                        id="canEditPaymentDueDate"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canEditPaymentDueDate" className="ml-2 block text-sm text-gray-900">
                        Edycja daty płatności
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        {...register('canUseCostCenter')}
                        type="checkbox"
                        id="canUseCostCenter"
                        className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label htmlFor="canUseCostCenter" className="ml-2 block text-sm text-gray-900">
                        Ośrodek kosztów
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Powiadomienia e-mail</h4>
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  {...register('emailOnAssignment', { value: true })}
                  type="checkbox"
                  id="emailOnAssignment"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="emailOnAssignment" className="ml-2 block text-sm text-gray-900">
                  Mail o otrzymaniu faktury (przekazanie)
                </label>
              </div>
              <div className="flex items-center">
                <input
                  {...register('emailOnReturn', { value: true })}
                  type="checkbox"
                  id="emailOnReturn"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="emailOnReturn" className="ml-2 block text-sm text-gray-900">
                  Mail o zwrocie faktury
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/app/users/list')}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Zapisywanie...</span>
                </span>
              ) : (
                'Dodaj użytkownika'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersAdd;
